import { useContext, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Body2 } from '@/components/ui/typography'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import { VerifyWalletRestrictionsByTradingAccountsResponseDto } from 'api/Wallet/Types/BulkRestrictionsDto'
import axios from 'axios'
import { errorToast, infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

interface Props {
  onClose(): void
}

interface TradingAccountPreviewItem {
  tradingAccountNumber: string
}

const initialPaginationState = { skip: 0, take: 10 }

export const BulkRestrictionsUploadModal = ({ onClose }: Props) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [fileInfo, setFileInfo] = useState<File>()
  const [preview, setPreview] = useState<VerifyWalletRestrictionsByTradingAccountsResponseDto>()
  const [paginationState, setPaginationState] = useState(initialPaginationState)
  const [isPreviewLoading, setPreviewLoading] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)

  const tradingAccounts = useMemo<TradingAccountPreviewItem[]>(
    () =>
      preview?.tradingAccountNumbers.map((tradingAccountNumber) => ({
        tradingAccountNumber,
      })) ?? [],
    [preview]
  )

  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setToast(infoToast('Supported format is only csv.'))
      return false
    }

    setFileInfo(file)
    setPreview(undefined)
    setPaginationState(initialPaginationState)
    return false
  }

  const handlePreview = async () => {
    if (!fileInfo) return

    try {
      setPreviewLoading(true)
      setPreview(undefined)

      const formData = new FormData()
      formData.append('files', fileInfo)

      const response = await walletsApiClient.previewWalletRestrictionsByTradingAccounts(formData)
      setPreview(response)
      setPaginationState(initialPaginationState)
    } catch (error: unknown) {
      console.error(error)
      setToast(errorToast(getErrorMessage(error, 'Error uploading trading accounts file.')))
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!fileInfo || !preview) return

    try {
      setSubmitting(true)

      const formData = new FormData()
      formData.append('files', fileInfo)

      const response = await walletsApiClient.applyWalletRestrictionsByTradingAccounts(formData)
      setToast(
        successToast(
          `${response.processedTradingAccountsCount} of ${response.allTradingAccountsCount} accounts processed successfully.`
        )
      )
      onClose()
    } catch (error: unknown) {
      console.error(error)
      setToast(errorToast(getErrorMessage(error, 'Error applying wallet restrictions.')))
    } finally {
      setSubmitting(false)
    }
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState({ skip: event.page.skip, take: event.page.take })
  }

  const isBusy = isPreviewLoading || isSubmitting

  return (
    <Dialog open onOpenChange={(open) => !open && !isBusy && onClose()}>
      <DialogContent size='lg'>
        <DialogHeader>
          <DialogTitle>Upload Trading Accounts</DialogTitle>
          <DialogDescription>
            The CSV file must include a TradingAccountNumber column.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-wrap items-center gap-3'>
          <Upload beforeUpload={handleSelectFile} showUploadList={false} accept='.csv'>
            <Button type='button' disabled={isBusy}>
              {fileInfo?.name ?? 'Select File ...'}
            </Button>
          </Upload>
          <Button type='button' onClick={handlePreview} disabled={!fileInfo || isBusy}>
            {isPreviewLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>

        {preview && (
          <>
            <Body2>
              {preview.existingTradingAccountNumberCount} of {tradingAccounts.length} Trading
              Accounts found.
            </Body2>
            <Grid
              resizable
              data={tradingAccounts.slice(
                paginationState.skip,
                paginationState.skip + paginationState.take
              )}
              skip={paginationState.skip}
              take={paginationState.take}
              total={tradingAccounts.length}
              onPageChange={handlePageChange}
              pageable={pageableSettings}
              style={{ zIndex: 0 }}
            >
              <Column
                field='tradingAccountNumber'
                title='Trading Account Number'
                minResizableWidth={240}
              />
            </Grid>
          </>
        )}

        <DialogFooter>
          <Button variant='outline' type='button' onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            type='button'
            onClick={handleSubmit}
            disabled={!preview || tradingAccounts.length === 0 || isBusy}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message ?? fallback
  }

  return 'An unexpected error occurred.'
}
